import { ChangeEvent, useState } from 'react';

import { IconButton, Tooltip } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { colors, Icon } from '@/utils';

import { CameraStreamControlButton } from './CameraStreamControlButton.tsx';
import {
  StyledDvrBar,
  StyledDvrEntryButtonHost,
  StyledDvrLabel,
  StyledDvrScrubber,
} from './cameraStreamWidget.styles.ts';
import { useCameraStreamFrameControlsContext } from './cameraStreamWidgetWrapper/cameraStreamFrameControlsProvider/CameraStreamFrameControlsContext.ts';
import type { UseHlsRewindPlayer } from './useHlsRewindPlayer.ts';

type PlaybackMode = 'live' | 'rewind';

type CameraStreamDvrControlsProps = {
  mode: PlaybackMode;
  rewind: UseHlsRewindPlayer;
  /** Advertised window length; used before the real seekable range is known. */
  fallbackWindowSeconds?: number;
  /** Enter rewind (if needed) and seek to `offsetFromLiveSeconds` behind live. */
  onScrub: (offsetFromLiveSeconds: number) => void;
  /** Return to the live WebRTC stream. */
  onGoLive: () => void;
  /** Capture the current rewind frame as a snapshot (DVR mode only). */
  onSnapshot?: () => void;
  zIndex: number;
};

const DEFAULT_WINDOW_SECONDS = 30;
const HISTORY_ENTRY_JUMP_SECONDS = 10;
const LIVE_EPSILON_SECONDS = 0.5;

/**
 * Short-term recall (DVR) controls. While live, the ONLY thing shown is the
 * "View Recent History" button — the sole entry point into rewind; the scrubber bar is
 * not rendered at all, so it can't clash with other controls on the bottom strip.
 * Pressing the button enters rewind and reveals the single-handle timeline (left = oldest
 * available, right = live edge); the LIVE button returns to the live WebRTC stream.
 */
export const CameraStreamDvrControls = ({
  mode,
  rewind,
  fallbackWindowSeconds,
  onScrub,
  onGoLive,
  onSnapshot,
  zIndex,
}: CameraStreamDvrControlsProps) => {
  const { t } = useTranslation('cameraControl');
  const { frameControlsVisible } = useCameraStreamFrameControlsContext();
  const [scrubValue, setScrubValue] = useState<number | null>(null);

  const isRewinding = mode === 'rewind';

  const windowSeconds = Math.max(
    1,
    Math.round(
      isRewinding && rewind.windowSeconds > 0
        ? rewind.windowSeconds
        : (fallbackWindowSeconds ?? DEFAULT_WINDOW_SECONDS)
    )
  );
  const offsetFromLive = isRewinding ? rewind.offsetFromLiveSeconds : 0;
  const atLive = !isRewinding || offsetFromLive <= LIVE_EPSILON_SECONDS;

  // Slider runs 0 (oldest) → windowSeconds (live). While dragging, the local value wins
  // so the controlled value doesn't fight the user's pointer.
  const liveAlignedValue = Math.max(0, Math.min(windowSeconds, windowSeconds - offsetFromLive));
  const sliderValue = scrubValue ?? liveAlignedValue;

  const handleScrubChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setScrubValue(value);
    onScrub(Math.max(0, windowSeconds - value));
  };

  const handleScrubCommit = () => setScrubValue(null);

  const handleHistoryEntry = () => onScrub(Math.min(HISTORY_ENTRY_JUMP_SECONDS, windowSeconds));

  // Live: sole entry point. Just the button
  if (!isRewinding) {
    return (
      <StyledDvrEntryButtonHost
        $zIndex={zIndex}
        $visible={frameControlsVisible}
        onClick={event => event.stopPropagation()}
      >
        <CameraStreamControlButton
          icon={<Icon.History size='large' />}
          label={t('dvr.viewHistory')}
          tooltipPlacement='top'
          onClick={handleHistoryEntry}
        />
      </StyledDvrEntryButtonHost>
    );
  }

  // Rewinding: the full scrubber bar (play/pause, timeline, offset, back-to-live).
  return (
    <StyledDvrBar
      $zIndex={zIndex}
      $visible
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
    >
      <Tooltip tooltip={rewind.isPaused ? t('dvr.play') : t('dvr.pause')} placement='top'>
        <IconButton
          aria-label={rewind.isPaused ? t('dvr.play') : t('dvr.pause')}
          variant='soft'
          size='large'
          Icon={
            rewind.isPaused ? (
              <Icon.CirclePlay size='large' color={colors.icon.action[100]} />
            ) : (
              <Icon.CirclePause size='large' color={colors.icon.action[100]} />
            )
          }
          onClick={rewind.togglePlay}
        />
      </Tooltip>

      {/* Snapshot: grab the currently shown rewind frame (session-only, in-browser). */}
      {onSnapshot && (
        <Tooltip tooltip={t('snapshot.take')} placement='top'>
          <IconButton
            aria-label={t('snapshot.take')}
            variant='soft'
            size='large'
            Icon={<Icon.Screenshot size='large' color={colors.icon.action[100]} />}
            onClick={onSnapshot}
          />
        </Tooltip>
      )}

      <StyledDvrScrubber
        type='range'
        min={0}
        max={windowSeconds}
        step={1}
        value={sliderValue}
        aria-label={t('dvr.scrubber')}
        onChange={handleScrubChange}
        onPointerUp={handleScrubCommit}
        onMouseUp={handleScrubCommit}
        onTouchEnd={handleScrubCommit}
      />

      <StyledDvrLabel>{atLive ? t('dvr.live') : formatOffset(offsetFromLive)}</StyledDvrLabel>

      {/* Back-to-live: return to the live WebRTC stream. */}
      <Tooltip tooltip={t('dvr.backToLive')} placement='top-end'>
        <IconButton
          aria-label={t('dvr.backToLive')}
          variant='soft'
          size='large'
          Icon={
            <Icon.Radio
              size='large'
              color={atLive ? colors.icon.action[100] : colors.icon.neutral[100]}
            />
          }
          onClick={onGoLive}
        />
      </Tooltip>
    </StyledDvrBar>
  );
};

//*********************************
// Helpers
//*********************************
const formatOffset = (seconds: number): string => {
  if (seconds < 60) return `-${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `-${minutes}:${String(remainder).padStart(2, '0')}`;
};
